import { CloseOutlined, PlusOutlined, StarFilled } from '@ant-design/icons';

import { SearchInput } from '@mm/components';
import { useClickOutside, useDebounce } from '@mm/hooks';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { assertUnreachable } from '@mm/utils';
import { ReactComponent as InstagramIcon } from 'assets/instagram.svg';
import { ReactComponent as FBIcon } from 'assets/messenger-fb-new.svg';
import { ReactComponent as SMSIcon } from 'assets/smartphone.svg';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useImportContactsPermission } from '../../../../../../../common/hooks/use-import-contacts-permission';
import { ReactComponent as GmailIcon } from '../../AutomationHome/TemplateIndex/gmail.svg';
import { InputTriggerModal } from './InputTriggerModal/InputTriggerModal';
import styles from './NewInputTriggerChooser.module.css';
import { TriggerCardDefinition } from './TriggerCardDefinition';
import {
  CATEGORIES,
  CATEGORIES_WITH_STAR_ICON,
  Category,
  CATEGORY_NAMES,
  disabledInputTriggerProps,
  TemplateParams,
  TRIGGER_DEFINITIONS,
  TRIGGERS_BY_CATEGORY,
  TriggerType,
  VISIBLE_TRIGGER_TYPES,
} from './TriggersDefinitions';
import {
  CONNECTABLE_CHANNELS,
  ConnectableChannel,
  useChannelConnection,
} from './use-channel-connection';
import { useDisabledInputTriggers } from './use-disabled-input-triggers';
import { useInputTriggerCRUD } from './use-input-trigger-crud';
import { useInputTriggersIdentification } from './use-input-triggers-identification';

interface Props {
  className?: string;
  campaignId: number;
  onClose: () => void;
  triggers: ReturnType<typeof useInputTriggersIdentification>;
  setAddingInputTrigger?: (ok: boolean) => void;
}

const colors = {
  blueGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #F7F0EF 15.24%, #CEE1FF 50%, #85B4EE 100%)',
  pinkGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #FFFAE4 4.17%, #FEC6E3 52.6%, #BFA8FF 100%)',
  limeGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #FEEFC7 5.58%, #D0F0D9 44.46%, #86C7C3 100%)',
  cyanGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #F3F7EF 4.17%, #A4E1DE 52.6%, #4BB4EF 100%)',
};

export const CONNECTABLE_CHANNEL_CARD_DEFINITIONS: Record<
  ConnectableChannel,
  TriggerCardDefinition
> = {
  facebook: {
    connectableChannelType: 'facebook',
    icon: <FBIcon width={38} height={38} />,
    name: 'Connect Facebook',
    nameComponent: (
      <PrimaryButton className={styles.connectChannelBtn}>
        Connect Facebook
      </PrimaryButton>
    ),
    description: 'and start generating more leads now!',
    descriptionClassName: styles.description,
    color: colors.blueGradient,
    hideAddButton: true,
    template: false,
  },
  instagram: {
    connectableChannelType: 'instagram',
    icon: <InstagramIcon width={38} height={38} />,
    name: 'Connect Instagram',
    nameComponent: (
      <PrimaryButton className={styles.connectChannelBtn}>
        Connect Instagram
      </PrimaryButton>
    ),
    description: 'and start generating more leads now!',
    descriptionClassName: styles.description,
    color: colors.pinkGradient,
    hideAddButton: true,
    template: false,
  },
  sms: {
    connectableChannelType: 'sms',
    icon: <SMSIcon width={38} height={38} />,
    name: 'Connect SMS',
    nameComponent: (
      <PrimaryButton className={styles.connectChannelBtn}>
        Claim Phone Number
      </PrimaryButton>
    ),
    description: 'and start re-engaging your leads with texts!',
    descriptionClassName: styles.description,
    color: colors.limeGradient,
    hideAddButton: true,
    template: false,
  },
  email: {
    connectableChannelType: 'email',
    icon: <GmailIcon width={38} height={38} />,
    name: 'Connect Gmail',
    nameComponent: (
      <PrimaryButton className={styles.connectChannelBtn}>
        Connect Gmail
      </PrimaryButton>
    ),
    description: 'and start re-engaging your leads with email!',
    descriptionClassName: styles.description,
    color: colors.cyanGradient,
    hideAddButton: true,
    template: false,
  },
};

const CONNECTABLE_CHANNELS_BY_CATGEGORY: Record<
  Category,
  null | ConnectableChannel[]
> = {
  all: null,
  featured: ['email', 'sms'],
  facebook: ['facebook'],
  import_contacts: ['sms'],
  instagram: ['instagram'],
  // other: [],
  sms: ['sms'],
  webchat: [],
};

export const NewInputTriggerChooser = (props: Props) => {
  const { className, campaignId, onClose, triggers, setAddingInputTrigger } =
    props;

  const disabledInputTriggers = useDisabledInputTriggers({
    onLinkClick: onClose,
  });

  const { createInputTriggerAndOpenSlideout } = useInputTriggerCRUD();

  const {
    isSMSConnected,
    isInstagramConnected,
    isFacebookConnected,
    isEmailConnected,
    connectChannel,
  } = useChannelConnection(() => setAddingInputTrigger?.(true));
  const ref = useClickOutside<HTMLInputElement>(onClose);
  const campaign = useSelector((state: StoreState) =>
    state.campaigns.entities.find(c => c.id === campaignId),
  );
  const enabledInputTriggers = useSelector(
    (state: StoreState) => state.campaignsOverview.enabledInputTriggers,
  );
  const featureFlags = useSelector(combinedFeatureFlagsSelector);

  const [selectedTriggerCard, setSelectedTriggerCard] =
    useState<TriggerCardDefinition>();
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [search, setSearch] = useState('');

  const q = useDebounce(search, 300);

  const importPermissions = useImportContactsPermission();

  const cards = useMemo(() => {
    const hideConnectedChannels = (
      connectableChannelType: ConnectableChannel,
    ) => {
      switch (connectableChannelType) {
        case 'instagram':
          return !isInstagramConnected;
        case 'facebook':
          return !isFacebookConnected;
        case 'sms':
          return !isSMSConnected;
        case 'email':
          return !isEmailConnected;
        default:
          return assertUnreachable(connectableChannelType);
      }
    };
    const connectCards: TriggerCardDefinition[] = (
      CONNECTABLE_CHANNELS_BY_CATGEGORY[activeCategory] || CONNECTABLE_CHANNELS
    )
      .filter(hideConnectedChannels)
      .map(connectableChannelType => ({
        ...CONNECTABLE_CHANNEL_CARD_DEFINITIONS[connectableChannelType],
      }));
    const hideByFeatureFlag = (triggerCard: TriggerCardDefinition) =>
      !triggerCard.requiredFeature || featureFlags[triggerCard.requiredFeature];
    const triggerCards = (
      TRIGGERS_BY_CATEGORY[activeCategory] || VISIBLE_TRIGGER_TYPES
    )
      .map<TriggerCardDefinition>(triggerType => ({
        ...TRIGGER_DEFINITIONS[triggerType],
        ...disabledInputTriggerProps({
          disabledInputTriggers,
          triggers,
          triggerType,
          isFacebookConnected,
          isInstagramConnected,
          isSMSConnected,
          isAllowedToImportContacts: importPermissions.isAllowed,
          isEmailConnected,
        }),
        triggerType,
        icon: React.createElement(TRIGGER_DEFINITIONS[triggerType].icon),
      }))
      .filter(hideByFeatureFlag);

    const byName = (a, b) => a.name.localeCompare(b.name);

    const featured = triggerCards.filter(x => x.featured);

    const popular = triggerCards.filter(x => x.popular).sort(byName);

    const regular = triggerCards
      .filter(x => !x.popular && !x.featured)
      .sort(byName);

    const sortedTriggerCards = [...featured, ...popular, ...regular];

    return [...connectCards, ...sortedTriggerCards];
  }, [
    activeCategory,
    featureFlags,
    isFacebookConnected,
    isInstagramConnected,
    isSMSConnected,
    isEmailConnected,
    triggers,
    importPermissions.isAllowed,
    disabledInputTriggers,
  ]);

  const filteredCards = useMemo(() => {
    return cards
      .filter(
        trigger =>
          !enabledInputTriggers?.find(input => input === trigger.triggerType),
      )
      .filter(triggerCard => {
        return [triggerCard.name, triggerCard.description].some(
          v =>
            typeof v === 'string' && v.toLowerCase().includes(q.toLowerCase()),
        );
      });
  }, [q, cards, enabledInputTriggers]);

  const onTriggerClick = useCallback(
    (triggerCard: TriggerCardDefinition) => {
      if (triggerCard.disabled) {
        return;
      }

      if (triggerCard.connectableChannelType) {
        connectChannel(triggerCard.connectableChannelType);
        onClose();
        return;
      }

      if (!triggerCard.template && triggerCard.triggerType) {
        if (!campaign) {
          return;
        }
        createInputTriggerAndOpenSlideout(campaign, triggerCard.triggerType);
        onClose();
        return;
      }

      setSelectedTriggerCard(triggerCard);
    },
    [campaign, connectChannel, createInputTriggerAndOpenSlideout, onClose],
  );

  if (!campaign) {
    return null;
  }
  const onTemplateParamsSubmitted = async (
    triggerType: TriggerType,
    params: TemplateParams,
  ) => {
    await createInputTriggerAndOpenSlideout(campaign, triggerType, params);
    onClose();
  };

  const TriggerList = (
    <div className={classNames(className, styles.container)}>
      <div className={styles.heading}>
        <div className={styles.headingText}>
          Choose an Automation Input Trigger
        </div>
        <span className={styles.closeButton} onClick={onClose}>
          <CloseOutlined />
        </span>
      </div>

      <div className={styles.search}>
        <SearchInput
          css={{ container: styles.searchInput }}
          placeholder="Search for automation"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className={styles.categories}>
        {CATEGORIES.map(categoryName => (
          <span
            key={categoryName}
            onClick={() => {
              setActiveCategory(categoryName);
              setSearch('');
            }}
            className={classNames(styles.category, {
              [styles.categoryActive]: activeCategory === categoryName,
            })}
          >
            {CATEGORIES_WITH_STAR_ICON.includes(categoryName) && <StarFilled />}
            {CATEGORY_NAMES[categoryName] || CATEGORY_NAMES.all}
          </span>
        ))}
      </div>

      <div
        className={classNames(styles.triggerList, {
          [styles.threeColumns]: activeCategory === 'featured',
        })}
      >
        {filteredCards.map(card => (
          <div
            key={card.triggerType || card.connectableChannelType}
            className={classNames(styles.trigger, {
              [styles.disabled]: card.disabled,
            })}
          >
            <div
              className={styles.card}
              style={{ background: card.color }}
              onClick={() => onTriggerClick(card)}
            >
              {card.popular && (
                <span className={styles.badge}>
                  <StarFilled />
                  Popular
                  <span className={styles.arrow} />
                </span>
              )}

              {card.featured && (
                <span className={classNames(styles.badge, styles.orange)}>
                  <StarFilled />
                  Featured
                  <span className={styles.arrow} />
                </span>
              )}

              {!card.hideAddButton && (
                <span className={styles.addBtn}>
                  <PlusOutlined />
                  Add
                </span>
              )}

              <div className={styles.triggerIcon}>{card.icon}</div>
              <div className={styles.triggerInfo}>
                <div className={styles.triggerName}>
                  {card.nameComponent || card.name}
                </div>
                <div
                  className={classNames(
                    styles.triggerDescription,
                    card.descriptionClassName,
                  )}
                >
                  {card.description}
                </div>
              </div>
            </div>
            {card.disabledTooltip && (
              <span className={styles.tooltip}>
                <span className={styles.tooltipContent}>
                  {card.disabledTooltip}
                </span>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const TriggerTemplate = selectedTriggerCard?.triggerType && (
    <InputTriggerModal
      campaignId={campaignId}
      triggerType={selectedTriggerCard.triggerType}
      onSubmit={onTemplateParamsSubmitted}
      onBack={() => setSelectedTriggerCard(undefined)}
      onClose={onClose}
    />
  );

  return (
    <div ref={ref} className={styles.modalContainer}>
      {selectedTriggerCard ? TriggerTemplate : TriggerList}
    </div>
  );
};
